/**
 * Storage, which here means the POLICY rather than the files.
 *
 * What a project stores is browsable with the CLI and the SDK. What only
 * this page can do is show, in one place, who may read each part of it -
 * and say what each change does before it is made.
 *
 * Deliberately not a file browser. Uploads, previews and pagination are a
 * different feature, and putting them here first would bury the one thing
 * on this page that can leak somebody's data.
 */

import { useCallback, useEffect, useState } from 'react'
import { useProject } from '../components/ProjectLayout.js'
import { PermissionButton } from '../components/Permissions.js'
import { CopyButton, Dialog, Empty, ErrorBox, Loading, PageHeader } from '../components/ui.js'
import { useApi } from '../lib/useApi.js'
import { ApiError, type StorageBucket } from '../lib/api.js'
import {
  ADOPT_PROMPT, DELETE_CONSEQUENCES, REVOKE_CONSEQUENCES,
  checkName, consequences, summarise, willRevoke, type Policy,
} from '../lib/buckets.js'
import { FileBrowser } from '../components/FileBrowser.js'

export default function Storage() {
  const { project } = useProject()
  const api = useApi()

  const [buckets, setBuckets] = useState<StorageBucket[] | null>(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [editing, setEditing] = useState<StorageBucket | 'new' | null>(null)
  const [name, setName] = useState('')
  const [policy, setPolicy] = useState<Policy>({ visibility: 'private', ownerScoped: false })
  const [adopt, setAdopt] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<StorageBucket | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<StorageBucket | null>(null)

  const load = useCallback(async () => {
    setBusy(true)
    try {
      setBuckets((await api.listBuckets(project.id)).buckets)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read the buckets.')
    } finally {
      setBusy(false)
    }
  }, [api, project.id])

  useEffect(() => { void load() }, [load])

  const existing = editing === 'new' ? null : editing

  async function save(e: React.FormEvent, withAdopt = false) {
    e.preventDefault()
    const target = existing?.name ?? name
    if (checkName(target)) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.putBucket(project.id, target.trim(), {
        ...policy, ...(withAdopt ? { adopt: true } : {}),
      })
      setNotice(result.note
        ?? (result.adopted
          ? `${target} now covers the files already under it. Nothing was moved.`
          : `${target} saved.`))
      setEditing(null)
      setAdopt(null)
      setName('')
      await load()
    } catch (err) {
      // The one refusal that is a question rather than a mistake.
      if (err instanceof ApiError && /prefix_in_use|already exist/i.test(err.message)) {
        setAdopt(target)
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not save the bucket.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function revoke(bucket: StorageBucket) {
    setBusy(true)
    try {
      await api.putBucket(project.id, bucket.name, { visibility: 'private', ownerScoped: bucket.ownerScoped })
      // Revoking through the close transition keeps one mechanism rather
      // than two: the server revokes when a public bucket goes private.
      if (bucket.visibility === 'public') {
        setNotice(`Every signed URL for ${bucket.name} has stopped working, and it is now private.`)
      } else {
        setNotice(`${bucket.name} was already private; its signed URLs were not changed.`)
      }
      setConfirmRevoke(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(bucket: StorageBucket) {
    setBusy(true)
    try {
      const result = await api.deleteBucket(project.id, bucket.name)
      setNotice(result.note)
      setConfirmDelete(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove the bucket.')
    } finally {
      setBusy(false)
    }
  }

  const problem = editing === 'new' ? checkName(name) : null

  return (
    <>
      <PageHeader
        title="Storage"
        description="Buckets decide who may read this project's files. Without one, every file needs a service key."
      />

      {error && <ErrorBox retry={load}>{error}</ErrorBox>}
      {notice && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <p className="sub small" style={{ margin: 0 }}>{notice}</p>
        </div>
      )}

      {busy && buckets === null ? <Loading /> : (
        <>
          {(buckets ?? []).length === 0 && editing === null && (
            <Empty>
              No buckets. Every file in this project needs a service key or a signed URL until you
              create one.
            </Empty>
          )}

          {(buckets ?? []).map((bucket) => (
            <section className="card panel" key={bucket.name} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 15 }}>{bucket.name}</h2>
                <span className={`pill ${bucket.visibility === 'public' ? 'pill-warn' : ''}`}>
                  {summarise(bucket)}
                </span>
                <span className="sub small">files under <code>{bucket.name}/</code></span>
              </div>

              <ul className="sub small" style={{ margin: '12px 0 0 18px' }}>
                {consequences(bucket).map((line) => <li key={line}>{line}</li>)}
              </ul>

              <div style={{ marginTop: 12 }}>
                <PermissionButton
                  permission="apikey:create" className="btn" disabled={busy}
                  onClick={() => {
                    setNotice(null)
                    setEditing(bucket)
                    setPolicy({ visibility: bucket.visibility, ownerScoped: bucket.ownerScoped })
                  }}
                >
                  Change who may read it
                </PermissionButton>
                {bucket.visibility === 'public' && (
                  <PermissionButton
                    permission="apikey:create" className="btn" style={{ marginLeft: 8 }} disabled={busy}
                    onClick={() => { setNotice(null); setConfirmRevoke(bucket) }}
                  >
                    Revoke signed URLs
                  </PermissionButton>
                )}
                <PermissionButton
                  permission="apikey:create" className="btn btn-danger" style={{ marginLeft: 8 }} disabled={busy}
                  onClick={() => { setNotice(null); setConfirmDelete(bucket) }}
                >
                  Remove
                </PermissionButton>
              </div>
            </section>
          ))}

          {editing === null ? (
            <PermissionButton
              permission="apikey:create" className="btn btn-primary"
              onClick={() => {
                setNotice(null)
                setEditing('new')
                setName('')
                setPolicy({ visibility: 'private', ownerScoped: false })
              }}
            >
              Create a bucket
            </PermissionButton>
          ) : (
            <form className="card panel" onSubmit={(e) => save(e)}>
              <h2 style={{ marginTop: 0, fontSize: 15 }}>
                {existing ? `Who may read ${existing.name}` : 'New bucket'}
              </h2>

              {existing === null && (
                <div className="field">
                  <label htmlFor="bucket-name">Name</label>
                  <input
                    id="bucket-name" value={name} autoCapitalize="off" spellCheck={false}
                    placeholder="avatars"
                    onChange={(e) => setName(e.target.value)}
                    style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }}
                  />
                  <p className="sub small" style={{ margin: '6px 0 0' }}>
                    It becomes the first part of every file&rsquo;s path: <code>{(name || 'avatars')}/photo.png</code>
                  </p>
                  {problem && <p className="sub small" style={{ margin: '6px 0 0' }}>{problem}</p>}
                </div>
              )}

              <div className="field">
                <label>
                  <input
                    type="checkbox" checked={policy.visibility === 'public'}
                    onChange={(e) => setPolicy({ ...policy, visibility: e.target.checked ? 'public' : 'private' })}
                  />{' '}
                  Anyone with a URL can read these files
                </label>
                <label style={{ display: 'block', marginTop: 8 }}>
                  <input
                    type="checkbox" checked={policy.ownerScoped}
                    onChange={(e) => setPolicy({ ...policy, ownerScoped: e.target.checked })}
                  />{' '}
                  Each signed-in user gets their own folder
                </label>
              </div>

              {/* The consequences of what is currently ticked, before saving. */}
              <ul className="sub small" style={{ margin: '0 0 12px 18px' }}>
                {consequences(policy).map((line) => <li key={line}>{line}</li>)}
              </ul>

              {willRevoke(existing, policy) && (
                <div className="notice notice-danger" style={{ marginBottom: 12 }}>
                  <p className="sub small" style={{ margin: 0 }}>
                    Making it private also revokes every signed URL for it. Links already shared stop
                    working, and there is no undo.
                  </p>
                </div>
              )}

              {adopt && (
                <div className="notice notice-danger" style={{ marginBottom: 12 }}>
                  <p className="sub small" style={{ margin: '0 0 8px' }}>{ADOPT_PROMPT}</p>
                  <button className="btn btn-danger" disabled={busy} onClick={(e) => save(e, true)}>
                    Apply it to the files already there
                  </button>
                </div>
              )}

              <PermissionButton
                permission="apikey:create" className="btn btn-primary"
                disabled={busy || problem !== null}
              >
                {busy ? 'Saving…' : 'Save'}
              </PermissionButton>
              <button
                type="button" className="btn" style={{ marginLeft: 8 }}
                onClick={() => { setEditing(null); setAdopt(null) }}
              >
                Cancel
              </button>
            </form>
          )}
        </>
      )}

      <Dialog
        open={confirmRevoke !== null}
        title={`Revoke signed URLs for ${confirmRevoke?.name ?? ''}?`}
        busy={busy}
        onClose={() => setConfirmRevoke(null)}
      >
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {REVOKE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
        </ul>
        <p className="sub small">This also makes the bucket private.</p>
        <button
          className="btn btn-danger" disabled={busy}
          onClick={() => confirmRevoke && revoke(confirmRevoke)}
        >
          {busy ? 'Revoking…' : 'Revoke every signed URL'}
        </button>
      </Dialog>

      <Dialog
        open={confirmDelete !== null}
        title={`Remove the bucket ${confirmDelete?.name ?? ''}?`}
        busy={busy}
        onClose={() => setConfirmDelete(null)}
      >
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {DELETE_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
        </ul>
        <button
          className="btn btn-danger" disabled={busy}
          onClick={() => confirmDelete && remove(confirmDelete)}
        >
          {busy ? 'Removing…' : 'Remove the bucket'}
        </button>
      </Dialog>

      <section className="panel" style={{ marginTop: 24 }}>
        <FileBrowser projectId={project.id} />
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Reaching these files from your app</h2>
        <p className="sub">
          The client uses the bucket name, and carries the signed-in user for you:
        </p>
        <div className="endpoint-line">
          <code>await nb.storage.from(&apos;uploads&apos;).upload(`${'${user.id}'}/cv.pdf`, file)</code>
          <CopyButton
            value="await nb.storage.from('uploads').upload(`${user.id}/cv.pdf`, file)"
            label="Copy example"
          />
        </div>
      </section>
    </>
  )
}
